// escape.ts
import { useEffect, useRef } from "react";

type EscapeHandler = (e: KeyboardEvent) => boolean | void;

export type EscapeOptions = {
  /** 높을수록 먼저 실행 */
  priority?: number;
  /** input/textarea/contentEditable에서도 동작할지 */
  allowInEditable?: boolean;
  /** 초기 enabled 상태 (기본 true) */
  enabled?: boolean;
};

type Entry = {
  id: number;
  handler: EscapeHandler;
  priority: number;
  allowInEditable: boolean;
  enabled: boolean;
};

class EscapeStack {
  private entries: Entry[] = [];
  private nextId = 1;
  private listening = false;

  // 쿨다운 관련 필드
  private lastHandledAt = 0;
  private cooldownMs = 350; // 기본 0.35초

  setCooldown(ms: number) {
    this.cooldownMs = ms;
  }

  /** 명령형 등록: 어디서든 호출 가능 (이벤트 핸들러/ useEffect 내부 포함) */
  on(handler: EscapeHandler, opts: EscapeOptions = {}) {
    const entry: Entry = {
      id: this.nextId++,
      handler,
      priority: opts.priority ?? 0,
      allowInEditable: !!opts.allowInEditable,
      enabled: opts.enabled ?? true,
    };
    this.entries.push(entry);
    this.sort();
    this.ensureListener();

    // 구독 객체 반환 (off/update/enable/disable/priority 변경)
    const api = {
      off: () => this.off(entry.id),
      update: (next: EscapeHandler) => (entry.handler = next),
      enable: () => (entry.enabled = true),
      disable: () => (entry.enabled = false),
      setPriority: (p: number) => {
        entry.priority = p;
        this.sort();
      },
    };
    return api;
  }

  private off(id: number) {
    this.entries = this.entries.filter((e) => e.id !== id);
    if (this.entries.length === 0) this.removeListener();
  }

  private sort() {
    this.entries.sort((a, b) => a.priority - b.priority); // 낮은→높은
  }

  private ensureListener() {
    if (this.listening) return;
    this.listening = true;
    document.addEventListener("keydown", this.onKeyDown, true); // capture
  }
  private removeListener() {
    if (!this.listening) return;
    this.listening = false;
    document.removeEventListener("keydown", this.onKeyDown, true);
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.defaultPrevented || e.key !== "Escape") return;

    // 쿨다운 검사
    const now = performance.now();
    const elapsed = now - this.lastHandledAt;
    if (elapsed < this.cooldownMs) {
      // 아직 쿨다운 중이면 무시
      return;
    }

    const t = e.target as HTMLElement | null;
    const isEditable =
      !!t &&
      (t.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName));

    // priority 높은 항목부터(배열 끝) 역순 처리
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const ent = this.entries[i];
      if (!ent.enabled) continue;
      if (isEditable && !ent.allowInEditable) continue;

      const handled = ent.handler?.(e);
      if (handled) {
        this.lastHandledAt = now; // 처리 완료 시점 기록
        e.preventDefault();
        e.stopPropagation();
        break;
      }
    }
  };
}

// 싱글톤 인스턴스 및 편의 함수
export const escapeStack = new EscapeStack();

/** 어디서든 import해 바로 사용: const sub = onEscape(() => true) */
export const onEscape = (handler: EscapeHandler, opts?: EscapeOptions) =>
  escapeStack.on(handler, opts);

export function useEscape(
  handler: () => boolean | void,
  opts: EscapeOptions = {},
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const sub = onEscape(() => handlerRef.current?.(), {
      priority: opts.priority,
      allowInEditable: opts.allowInEditable,
      enabled: opts.enabled ?? true,
    });
    return () => sub.off();
  }, [handler, opts.priority, opts.allowInEditable, opts.enabled]);
}
